import React, { useEffect, useState } from 'react';
import {
  Popover,
  Box,
  Typography,
  Chip,
  RadioGroup,
  Radio,
  FormControlLabel,
  TextField,
  Divider,
  Button,
  alpha
} from '@mui/material';
import { AttendanceStatus, InformationType } from '../../types';

// ============================================================================
// Cell palette popover.
//
// Anchored next to a single cell while the user is in Mark Mode. Use this
// when the user wants more nuance than a click-cycle gives: full status
// palette, informed/uninformed toggle, optional remarks. Writes are staged
// into the page-level draft (never hits the server here) — the floating
// MarkBar is the only thing that commits.
// ============================================================================

const STATUS_OPTIONS: { value: AttendanceStatus; label: string; color: string }[] = [
  { value: AttendanceStatus.PRESENT,  label: 'Present',   color: '#22c55e' },
  { value: AttendanceStatus.LATE,     label: 'Late',      color: '#f59e0b' },
  { value: AttendanceStatus.HALF_DAY, label: 'Half Day',  color: '#3b82f6' },
  { value: AttendanceStatus.ABSENT,   label: 'Absent',    color: '#ef4444' },
  { value: AttendanceStatus.LEAVE,    label: 'Leave',     color: '#06b6d4' }
];

const NEEDS_INFO_TYPE = new Set<AttendanceStatus>([
  AttendanceStatus.LATE,
  AttendanceStatus.HALF_DAY,
  AttendanceStatus.ABSENT
]);

export interface CellPaletteValue {
  status: AttendanceStatus;
  informationType?: InformationType;
  remarks?: string;
}

interface Props {
  open: boolean;
  anchorEl: HTMLElement | null;
  // Generic display labels — the popover is reused across both student and
  // employee attendance, so we don't bake in "Roll #" semantics here.
  title: string;     // primary heading (e.g. student or employee name)
  subtitle?: string; // secondary line (e.g. "Roll #42" or "Math Teacher")
  initial: CellPaletteValue;
  onClose: () => void;
  onApply: (value: CellPaletteValue) => void;
  // When present, the palette shows a "Not marked" action that removes any
  // staged mark for this cell. Meant for callers that want a skip / opt-out
  // affordance (e.g. student attendance where "don't record anything for
  // this student today" is a valid outcome). Omit for callers that require
  // every cell to carry a status.
  onClear?: () => void;
}

const AttendanceCellPalette: React.FC<Props> = ({
  open,
  anchorEl,
  title,
  subtitle,
  initial,
  onClose,
  onApply,
  onClear
}) => {
  const [status, setStatus] = useState<AttendanceStatus>(initial.status);
  const [informationType, setInformationType] = useState<InformationType | undefined>(initial.informationType);
  const [remarks, setRemarks] = useState<string>(initial.remarks ?? '');

  useEffect(() => {
    if (open) {
      setStatus(initial.status);
      setInformationType(initial.informationType);
      setRemarks(initial.remarks ?? '');
    }
  }, [open, initial.status, initial.informationType, initial.remarks]);

  const needsInfoType = NEEDS_INFO_TYPE.has(status);
  const disabled = needsInfoType && !informationType;

  const handleApply = () => {
    onApply({
      status,
      informationType: needsInfoType ? informationType : undefined,
      remarks: remarks.trim() || undefined
    });
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            p: 2,
            borderRadius: 3,
            minWidth: 320,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            boxShadow: (theme) => `0 12px 32px ${alpha(theme.palette.common.black, 0.18)}`
          }
        }
      }}
    >
      <Box mb={1.5}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase', fontSize: '0.65rem' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.1 }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}>
        Status
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={0.75} mb={1.5}>
        {STATUS_OPTIONS.map(opt => (
          <Chip
            key={opt.value}
            label={opt.label}
            onClick={() => setStatus(opt.value)}
            size="small"
            variant={status === opt.value ? 'filled' : 'outlined'}
            sx={{
              bgcolor: status === opt.value ? opt.color : 'transparent',
              color: status === opt.value ? '#fff' : opt.color,
              borderColor: opt.color,
              fontWeight: 600,
              '&:hover': {
                bgcolor: status === opt.value ? opt.color : alpha(opt.color, 0.1)
              }
            }}
          />
        ))}
      </Box>

      {needsInfoType && (
        <Box sx={{ mb: 1.5, p: 1.25, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontWeight: 600 }}>
            Type
          </Typography>
          <RadioGroup
            row
            value={informationType || ''}
            onChange={(e) => setInformationType(e.target.value as InformationType)}
          >
            <FormControlLabel
              value={InformationType.INFORMED}
              control={<Radio size="small" />}
              label={<Typography variant="body2">Informed</Typography>}
            />
            <FormControlLabel
              value={InformationType.UNINFORMED}
              control={<Radio size="small" />}
              label={<Typography variant="body2">Uninformed</Typography>}
            />
          </RadioGroup>
        </Box>
      )}

      <TextField
        fullWidth
        size="small"
        placeholder="Optional remarks"
        multiline
        rows={2}
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />

      <Divider sx={{ mb: 1.25 }} />
      <Box display="flex" alignItems="center" gap={1}>
        {onClear && (
          <Button
            size="small"
            onClick={onClear}
            sx={{ borderRadius: 2, textTransform: 'none', color: 'text.secondary' }}
          >
            Not marked
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleApply}
          disabled={disabled}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 2 }}
        >
          Apply
        </Button>
      </Box>
    </Popover>
  );
};

export default AttendanceCellPalette;
